import { SGR_DEFAULT_RATES } from 'lib/consts';

export interface RiskPremiumRates {
  variableRate?: number;
  fixedRate?: number;
  variableDiscount?: number;
  fixedDiscount?: number;
}

export interface RiskPremiumCalculationInput {
  amount: number;
  days: number;
  conversionRate: number;
  type: string;
  rates?: RiskPremiumRates;
  subProductsData?: any[];
  useConcentrationRates?: boolean;
}

export interface RiskPremiumCalculationResult {
  amountInCLP: number;
  financialYear: number;
  guaranteeAmount: number;
  effectiveVariableRate: number;
  effectiveFixedRate: number;
  variableComponent: number;
  fixedComponent: number;
  totalPremium: number;
  variableDiscount: number;
  fixedDiscount: number;
  originalVariableRate: number;
  originalFixedRate: number;
}

/**
 * Calcula la prima de riesgo SGR de forma estandarizada para ambas calculadoras.
 * @param input Parámetros de cálculo
 * @returns Detalle del cálculo de prima de riesgo
 */
export function calculateRiskPremium(
  input: RiskPremiumCalculationInput,
): RiskPremiumCalculationResult | null {
  const {
    amount,
    days,
    conversionRate,
    type,
    rates = {},
    subProductsData = [],
    useConcentrationRates = false,
  } = input;

  if (
    !(
      amount > 0 &&
      days > 0 &&
      conversionRate > 0 &&
      type &&
      type.trim() !== ''
    )
  ) {
    return null;
  }

  // Buscar tasas en subProductsData solo si useConcentrationRates es true
  let variableRate = SGR_DEFAULT_RATES.variableRate;
  let fixedRate = SGR_DEFAULT_RATES.fixedRate;
  let variableDiscount = SGR_DEFAULT_RATES.variableDiscount;
  let fixedDiscount = SGR_DEFAULT_RATES.fixedDiscount;

  if (
    useConcentrationRates &&
    Array.isArray(subProductsData) &&
    subProductsData.length > 0
  ) {
    const subProduct = subProductsData.find(
      (sp: any) => sp.subProductId === type,
    );
    if (subProduct) {
      variableRate = subProduct.operationCostVariable ?? variableRate;
      fixedRate = subProduct.operationCostFixed ?? fixedRate;
      variableDiscount = subProduct.discountRateVariable ?? variableDiscount;
      fixedDiscount = subProduct.discountRateFixed ?? fixedDiscount;
    }
  }

  // Permitir override manual por rates
  variableRate = rates.variableRate ?? variableRate;
  fixedRate = rates.fixedRate ?? fixedRate;
  variableDiscount = rates.variableDiscount ?? variableDiscount;
  fixedDiscount = rates.fixedDiscount ?? fixedDiscount;

  const amountInCLP = amount * conversionRate;
  const financialYear = days / 360;
  const guaranteeAmount = amountInCLP * financialYear;

  const effectiveVariableRate = variableRate * (1 - variableDiscount / 100);
  const effectiveFixedRate = fixedRate * (1 - fixedDiscount / 100);

  const variableComponent = guaranteeAmount * (effectiveVariableRate / 100);
  const fixedComponent = effectiveFixedRate;
  const totalPremium = variableComponent + fixedComponent;

  return {
    amountInCLP,
    financialYear,
    guaranteeAmount,
    effectiveVariableRate,
    effectiveFixedRate,
    variableComponent,
    fixedComponent,
    totalPremium,
    variableDiscount,
    fixedDiscount,
    originalVariableRate: variableRate,
    originalFixedRate: fixedRate,
  };
}
